import { useMemo } from "react";

// Deterministic-ish random for consistent star/coin layouts
function rand(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function StarField({ count = 90, seed = 7 }) {
  const stars = useMemo(() => {
    const r = rand(seed);
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: r() * 100,
      y: r() * 100,
      size: 0.6 + r() * 1.8,
      opacity: 0.3 + r() * 0.55,
      twinkle: 2 + r() * 5,
      delay: r() * 6,
    }));
  }, [count, seed]);
  return (
    <svg
      className="bg-stars"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {stars.map((s) => (
        <circle
          key={s.id}
          cx={s.x}
          cy={s.y}
          r={s.size * 0.05}
          fill="#fff5d6"
          opacity={s.opacity}
          style={{
            animation: `bgTwinkle ${s.twinkle}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
    </svg>
  );
}

function HennaRing({ size = 720, opacity = 0.12, top = "20%", left = "-15%", spin = 220, reverse = false }) {
  // Concentric ornamental rings — evokes mehndi/mandala without being literal
  const petals = 24;
  const petalArr = Array.from({ length: petals });
  return (
    <svg
      className="bg-henna"
      viewBox="0 0 200 200"
      style={{
        width: size,
        height: size,
        top,
        left,
        opacity,
        animation: `bgSpin ${spin}s linear ${reverse ? "reverse" : "normal"} infinite`,
      }}
      aria-hidden="true"
    >
      <g stroke="#e8b86b" fill="none" strokeWidth="0.4">
        <circle cx="100" cy="100" r="92" />
        <circle cx="100" cy="100" r="76" strokeDasharray="1 2" />
        <circle cx="100" cy="100" r="60" />
        <circle cx="100" cy="100" r="44" strokeDasharray="0.8 1.6" />
        <circle cx="100" cy="100" r="28" />
      </g>
      <g stroke="#e8b86b" fill="none" strokeWidth="0.5">
        {petalArr.map((_, i) => {
          const a = (i / petals) * Math.PI * 2;
          const x1 = 100 + Math.cos(a) * 60;
          const y1 = 100 + Math.sin(a) * 60;
          const x2 = 100 + Math.cos(a) * 92;
          const y2 = 100 + Math.sin(a) * 92;
          const cxm = 100 + Math.cos(a) * 76;
          const cym = 100 + Math.sin(a) * 76;
          return (
            <g key={i}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} />
              <circle cx={cxm} cy={cym} r="1.2" fill="#e8b86b" stroke="none" />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function SilkRibbons() {
  // Three flowing ribbon paths at different positions
  return (
    <svg
      className="bg-silk"
      viewBox="0 0 100 600"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="silk1" x1="0" y1="0" x2="1" y2="0.3">
          <stop offset="0%" stopColor="#ff8a9b" stopOpacity="0" />
          <stop offset="50%" stopColor="#e23a5e" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#7a1530" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="silk2" x1="0" y1="0" x2="1" y2="0.3">
          <stop offset="0%" stopColor="#f7c87a" stopOpacity="0" />
          <stop offset="50%" stopColor="#e6a13a" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#7e4c10" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="silk3" x1="0" y1="0" x2="1" y2="0.3">
          <stop offset="0%" stopColor="#c89bff" stopOpacity="0" />
          <stop offset="50%" stopColor="#8a4ccc" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#3a1758" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M -10,40 C 20,80 40,10 60,60 C 80,100 110,30 110,30"
        stroke="url(#silk1)"
        strokeWidth="2.5"
        fill="none"
        style={{ animation: "ribbonDrift1 18s ease-in-out infinite alternate" }}
      />
      <path
        d="M -10,220 C 25,180 45,260 65,210 C 85,170 110,240 110,240"
        stroke="url(#silk2)"
        strokeWidth="2.2"
        fill="none"
        style={{ animation: "ribbonDrift2 22s ease-in-out infinite alternate" }}
      />
      <path
        d="M -10,420 C 25,460 50,380 70,440 C 90,490 110,400 110,400"
        stroke="url(#silk3)"
        strokeWidth="2.2"
        fill="none"
        style={{ animation: "ribbonDrift3 26s ease-in-out infinite alternate" }}
      />
    </svg>
  );
}

function FloatingCoins({ count = 18, seed = 21 }) {
  const coins = useMemo(() => {
    const r = rand(seed);
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: r() * 100,
      top: r() * 100,
      size: 8 + r() * 14,
      drift: 6 + r() * 10,
      delay: r() * 12,
      hueShift: r() * 30 - 15,
    }));
  }, [count, seed]);
  return (
    <div className="bg-coins" aria-hidden="true">
      {coins.map((c) => (
        <span
          key={c.id}
          className="bg-coin"
          style={{
            left: `${c.left}%`,
            top: `${c.top}%`,
            width: c.size,
            height: c.size,
            animation: `coinFloat ${c.drift}s ease-in-out ${c.delay}s infinite alternate`,
            filter: `hue-rotate(${c.hueShift}deg)`,
          }}
        />
      ))}
    </div>
  );
}

function SparkleDust({ count = 60, seed = 33 }) {
  const dots = useMemo(() => {
    const r = rand(seed);
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: r() * 100,
      top: r() * 100,
      size: 1 + r() * 2,
      delay: r() * 4,
      dur: 1.6 + r() * 2.4,
    }));
  }, [count, seed]);
  return (
    <div className="bg-sparkles" aria-hidden="true">
      {dots.map((d) => (
        <span
          key={d.id}
          className="bg-sparkle"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: d.size,
            height: d.size,
            animation: `sparkleBlink ${d.dur}s ease-in-out ${d.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

export default function ParallaxBackground() {
  return (
    <div className="parallax-bg" aria-hidden="true">
      {/* Layer 0: warm gradient washes painted on .parallax-bg itself (CSS) */}

      {/* Layer 2: distant stars (parallax slow) */}
      <div className="bg-layer bg-layer-stars">
        <StarField count={50} seed={7} />
      </div>

      {/* Layer 3: henna ornament rings — viewport-anchored, no parallax */}
      <div className="bg-layer bg-layer-henna">
        <HennaRing size={760} top="-20%" left="-22%" opacity={0.14} spin={260} />
        <HennaRing
          size={520}
          top="55%"
          left="70%"
          opacity={0.12}
          spin={200}
          reverse
        />
      </div>

      {/* Layer 4: silk ribbons (parallax medium) */}
      <div className="bg-layer bg-layer-silk">
        <SilkRibbons />
      </div>

      {/* Layer 5: floating coins (parallax fast) */}
      <div className="bg-layer bg-layer-coins">
        <FloatingCoins count={10} seed={21} />
      </div>

      {/* Layer 6: sparkle dust (parallax fastest, foreground hint) */}
      <div className="bg-layer bg-layer-sparkles">
        <SparkleDust count={28} seed={33} />
      </div>

      {/* Vignette */}
      <div className="bg-vignette" />
    </div>
  );
}
